import React, { createContext, ReactNode, useReducer } from 'react';
import { AppError } from '@/domain/ErrorApp.ts';
import { ItemMenu } from '@/domain/menu.ts';
import { SingleTab } from '@/domain/singleTab.ts';
import { notifyBeforeActiveTabChange } from '@/shared/common/Editor/editorTabLifecycle';
import {
  GlobalAction,
  setActiveTabAction,
  setErrorAction,
  setIsDarkAction,
  setReloadProjectListAction,
  setStateVideoAction,
  setTabAction,
  setTabsAction,
  setTabsPageMapAction,
} from './actions';
import { globalReducer } from './reducer';
import { GlobalState, initialState } from './state';

interface GlobalContextProps extends GlobalState {
  setIsDark: (isDark: boolean) => void;
  setAppError: (error?: AppError) => void;
  setTabs: (tab: SingleTab[]) => void;
  setTab: (tab: SingleTab) => void;
  setTabsPageMap: (payload: Record<string, ItemMenu>) => void;
  setActiveTab: (key: string) => void;
  setStateVideo: (stateVideo: boolean) => void;
  setReloadProjectList: (reloadProjectList: boolean) => void;
  dispatch: React.Dispatch<GlobalAction>;
}

const noop = () => undefined;

export const Context = createContext<GlobalContextProps>({
  ...initialState,
  setIsDark: noop,
  setAppError: noop,
  setTabs: noop,
  setTab: noop,
  setTabsPageMap: noop,
  setActiveTab: noop,
  setStateVideo: noop,
  setReloadProjectList: noop,
  dispatch: noop,
});

interface GlobalProviderProps {
  children: ReactNode;
}

export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(globalReducer, initialState);

  const setIsDark = (isDark: boolean) => {
    dispatch(setIsDarkAction(isDark));
  };

  const setAppError = (error?: AppError) => {
    dispatch(setErrorAction(error));
  };

  const setTabs = (tab: SingleTab[]) => {
    dispatch(setTabsAction(tab));
  };

  const setTab = (tab: SingleTab) => {
    dispatch(setTabAction(tab));
  };

  const setTabsPageMap = (payload: Record<string, ItemMenu>) => {
    dispatch(setTabsPageMapAction(payload));
  };

  const setActiveTab = (key: string) => {
    notifyBeforeActiveTabChange(state.activeTab, key);
    dispatch(setActiveTabAction(key));
  };

  const setStateVideo = (stateVideo: boolean) => {
    dispatch(setStateVideoAction(stateVideo));
  };

  const setReloadProjectList = (reloadProjectList: boolean) => {
    dispatch(setReloadProjectListAction(reloadProjectList));
  };

  return (
    <Context.Provider
      value={{
        ...state,
        setIsDark,
        setAppError,
        setTabs,
        setTab,
        setTabsPageMap,
        setActiveTab,
        setStateVideo,
        setReloadProjectList,
        dispatch,
      }}
    >
      {children}
    </Context.Provider>
  );
};
